import React, { useEffect, useState } from 'react';
import { useKnowledgeBaseStore } from '../../../../store/knowledgeBaseStore';
import sharedStyles from '../../shared/TicketDetailModal.module.css';

/**
 * "+ New Article" modal for the call-center Knowledge Base.
 *
 * POSTs to /api/v1/knowledge-base via the KB store's `create`. The slug is
 * auto-derived from the title (lowercase, hyphenated) so the agent doesn't
 * have to think about it; they can override if needed by editing later.
 */

interface Props {
  open: boolean;
  /** Optional list of existing categories so the dropdown can suggest them. */
  knownCategories?: string[];
  onClose: () => void;
  onCreated?: (id: number) => void;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
}

export const CreateArticleModal: React.FC<Props> = ({
  open, knownCategories = [], onClose, onCreated,
}) => {
  const create = useKnowledgeBaseStore((s) => s.create);
  const saving = useKnowledgeBaseStore((s) => s.saving);

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');
  const [readTime, setReadTime] = useState('3');
  const [publish, setPublish] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setTitle(''); setCategory(''); setBody(''); setTags('');
      setReadTime('3'); setPublish(true); setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError('Title is required'); return; }
    if (!category.trim()) { setError('Category is required'); return; }
    if (!body.trim()) { setError('Article body is required'); return; }
    setError(null);
    try {
      const created = await create({
        slug: slugify(title),
        title: title.trim(),
        category: category.trim(),
        body: body.trim(),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
        read_time_minutes: Math.max(1, parseInt(readTime, 10) || 3),
        is_published: publish,
      });
      onCreated?.(created.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create article');
    }
  };

  return (
    <div className={sharedStyles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div
        className={sharedStyles.modal}
        style={{ maxWidth: 640 }}
        onClick={(e) => e.stopPropagation()}
      >
        <header className={sharedStyles.header}>
          <div className={sharedStyles.headerLeft}>
            <span className={sharedStyles.ticketBadge}>New Knowledge Article</span>
          </div>
          <button className={sharedStyles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form
          onSubmit={handleSubmit}
          className={sharedStyles.body}
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {error && (
            <p style={{ color: '#B91C1C', background: '#FEE2E2', padding: 8, borderRadius: 6, margin: 0, fontSize: 13 }}>
              {error}
            </p>
          )}

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>
            <span>Title *</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={150}
              placeholder="e.g. How to process a partial refund"
              style={{ padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
            />
            {title && (
              <small style={{ color: '#9CA3AF', fontWeight: 400 }}>
                slug: {slugify(title) || '(empty)'}
              </small>
            )}
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>
              <span>Category *</span>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                list="kb-categories"
                placeholder="e.g. Refunds, Disputes, Returns…"
                style={{ padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
              />
              <datalist id="kb-categories">
                {knownCategories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>
              <span>Read time (min)</span>
              <input
                type="number"
                min={1}
                max={60}
                value={readTime}
                onChange={(e) => setReadTime(e.target.value)}
                style={{ padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
              />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>
            <span>Article Body *</span>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              rows={8}
              placeholder="Markdown supported. Steps, screenshots, escalation paths…"
              style={{ padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' }}
            />
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#374151' }}>
            <span>Tags <small style={{ fontWeight: 400, color: '#9CA3AF' }}>(comma separated)</small></span>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="refund, dispute, escalation"
              style={{ padding: 8, borderRadius: 6, border: '1px solid #D1D5DB', fontSize: 13 }}
            />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            <span>Publish immediately (otherwise saves as draft)</span>
          </label>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#2563EB', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Create Article'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateArticleModal;
